import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'
import { initialMoney, Money } from '@/modules/shared/utils/money'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

export type BookValueSummary = {
  beginning_balance: Money
  contributions: Money
  distributions: Money
  total_cash: Money
  operating_income_or_expense: Money
  management_fee: Money
  other_called_fee: Money
  unrealized_gain: Money
  realized_gain: Money
  interest_earned: Money
  carried_interest: Money
  other_distributed_fee: Money
  total_operating_profit_and_loss: Money
  syndication_fee: Money
  ending_balance: Money
}

export type CommitmentSummary = {
  total_commitment: Money
  initial_commitment: Money
  additional_commitments: Money
  contributions: Money
  ending_unfunded_commitment: Money
}

export type CommitmentSummaryPeriods = {
  since_inception: CommitmentSummary
  year_to_date: CommitmentSummary
  Q1: CommitmentSummary
  Q2: CommitmentSummary
  Q3: CommitmentSummary
  Q4: CommitmentSummary
}

export type BookValue = {
  year_options: number[]
  since_inception: BookValueSummary
  year_to_date: BookValueSummary
  Q1: BookValueSummary
  Q2: BookValueSummary
  Q3: BookValueSummary
  Q4: BookValueSummary
  commitment_summary: CommitmentSummaryPeriods
}

const bookValueSummaryInitialState: BookValueSummary = {
  beginning_balance: initialMoney,
  contributions: initialMoney,
  distributions: initialMoney,
  total_cash: initialMoney,
  operating_income_or_expense: initialMoney,
  management_fee: initialMoney,
  other_called_fee: initialMoney,
  unrealized_gain: initialMoney,
  realized_gain: initialMoney,
  interest_earned: initialMoney,
  carried_interest: initialMoney,
  other_distributed_fee: initialMoney,
  total_operating_profit_and_loss: initialMoney,
  syndication_fee: initialMoney,
  ending_balance: initialMoney,
}

const commitmentSummaryInitialState: CommitmentSummary = {
  total_commitment: initialMoney,
  initial_commitment: initialMoney,
  additional_commitments: initialMoney,
  contributions: initialMoney,
  ending_unfunded_commitment: initialMoney,
}

const commitmentSummaryPeriodsInitialState: CommitmentSummaryPeriods = {
  since_inception: { ...commitmentSummaryInitialState },
  year_to_date: { ...commitmentSummaryInitialState },
  Q1: { ...commitmentSummaryInitialState },
  Q2: { ...commitmentSummaryInitialState },
  Q3: { ...commitmentSummaryInitialState },
  Q4: { ...commitmentSummaryInitialState },
}

const bookValueInitialState: BookValue = {
  year_options: [],
  since_inception: { ...bookValueSummaryInitialState },
  year_to_date: { ...bookValueSummaryInitialState },
  Q1: { ...bookValueSummaryInitialState },
  Q2: { ...bookValueSummaryInitialState },
  Q3: { ...bookValueSummaryInitialState },
  Q4: { ...bookValueSummaryInitialState },
  commitment_summary: { ...commitmentSummaryPeriodsInitialState },
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

export const useCapitalAccountStore = defineStore('investing/capitalAccountStore', () => {
  const bookValue = ref<BookValue>({ ...bookValueInitialState })

  const fetchCapitalAccount = async (
    type: string,
    id: string,
    { slug, shareholder_cids, year }: { slug: string; shareholder_cids?: string[]; year: number },
  ) => {
    const params = new URLSearchParams()
    params.set('cid', `${type}:${id}`)
    params.set('year', year.toString())
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))

    const queryParams = params.toString() ? `?${params}` : ''
    const data = await useFetch(`/v4/${slug}/investing/capital-accounts${queryParams}`).get<BookValue>()
    bookValue.value = data
  }

  return {
    bookValue,
    fetchCapitalAccount,
  }
})
